import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export const SERVICE_TYPES = {
    hosting: { label: 'Hospedagem', icon: 'Home', color: 'bg-blue-100 text-blue-700', priceUnit: 'day', priceLabel: 'diária' },
    walking: { label: 'Passeio', icon: 'Footprints', color: 'bg-green-100 text-green-700', priceUnit: 'hour', priceLabel: 'hora' },
    daycare: { label: 'Cuidados Diários', icon: 'Heart', color: 'bg-amber-100 text-amber-700', priceUnit: 'day', priceLabel: 'diária' },
    taxi_dog: { label: 'Táxi Dog', icon: 'Car', color: 'bg-pink-100 text-pink-700', priceUnit: 'km', priceLabel: 'km' },
    partner: { label: 'Parceria', icon: 'Building2', color: 'bg-[#4A90B2]/20 text-[#4A90B2]', priceUnit: 'service', priceLabel: 'serviço' },
    others: { label: 'Outros', icon: 'MoreHorizontal', color: 'bg-slate-100 text-slate-700', priceUnit: 'service', priceLabel: 'serviço' },
};

export const USER_TYPES = {
    tutor: { label: 'Cliente', description: 'Sou dono de pet' },
    host: { label: 'Anfitrião', description: 'Ofereço hospedagem' },
    caregiver: { label: 'Cuidador/Passeador', description: 'Ofereço cuidados e passeios' },
    driver: { label: 'Motorista', description: 'Ofereço Táxi Dog' },
    partner: { label: 'Parceria', description: 'Veterinário, Petshop, etc.' },
};

export const BOOKING_STATUS = {
    pending: { label: 'Aguardando Aceite', color: 'bg-amber-100 text-amber-700', description: 'Aguardando resposta do prestador (24h)' },
    awaiting_payment: { label: 'Aguardando Pagamento', color: 'bg-orange-100 text-orange-700', description: 'Aceito! Efetue o pagamento em 24h' },
    confirmed: { label: 'Pago e Confirmado', color: 'bg-blue-100 text-blue-700', description: 'Pagamento confirmado' },
    in_progress: { label: 'Em Andamento', color: 'bg-[#4A90B2]/20 text-[#4A90B2]', description: 'Serviço em execução' },
    completed: { label: 'Concluída', color: 'bg-green-100 text-green-700', description: 'Serviço finalizado' },
    cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700', description: 'Reserva cancelada' },
    expired: { label: 'Expirada', color: 'bg-gray-100 text-gray-700', description: 'Expirou por falta de resposta ou pagamento' },
};

export const APPROVAL_STATUS = {
    pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Aprovado', color: 'bg-green-100 text-green-700' },
    rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-700' },
};

export const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value);
};

export const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Se a data vier no formato YYYY-MM-DD (sem timezone), tratar como data local
    // para evitar shift de fuso horário
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }
    
    // Para datas com timezone (ISO 8601), usar timezone de São Paulo
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
    });
};

export const formatDateTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
    });
};

// Função auxiliar para formatar data sem shift de timezone
export const formatLocalDate = (dateString) => {
    if (!dateString) return '';
    
    // Sempre tratar como data local (YYYY-MM-DD)
    const cleanDate = dateString.split('T')[0]; // Remove parte de hora se existir
    const [year, month, day] = cleanDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

export const getInitials = (name) => {
    if (!name) return '??';
    return name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
};

export const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
};

// CPF Validation
export const validateCPF = (cpf) => {
    // Remove non-digits
    const cleanCPF = cpf.replace(/\D/g, '');
    
    // Must have 11 digits
    if (cleanCPF.length !== 11) return false;
    
    // Check for known invalid CPFs (all same digits)
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
    
    // Validate first check digit
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cleanCPF[i]) * (10 - i);
    }
    let digit1 = (sum * 10 % 11) % 10;
    if (digit1 !== parseInt(cleanCPF[9])) return false;
    
    // Validate second check digit
    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cleanCPF[i]) * (11 - i);
    }
    let digit2 = (sum * 10 % 11) % 10;
    if (digit2 !== parseInt(cleanCPF[10])) return false;
    
    return true;
};

export const formatCPF = (cpf) => {
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length <= 3) return cleanCPF;
    if (cleanCPF.length <= 6) return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3)}`;
    if (cleanCPF.length <= 9) return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6)}`;
    return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6, 9)}-${cleanCPF.slice(9, 11)}`;
};

export const maskCPF = (value) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 11);
    return formatCPF(cleanValue);
};
